/**
 * VendorRegistrySection — iter85.4 / iter85.5 · relocated iter86.29
 * (working list managed in Console → Vendors; read-only dependency view
 * in Canon → Features).
 *
 * Manual registry of every third-party service (paid AND free sources),
 * grouped by area, with billing cadence, $ cost, renewal radar, and
 * impacted feature/env tags. Rows with a live-status integration
 * (API-Sports plan/quota, Resend domain verification) get an inline
 * "Live status" button. Every change write-throughs to
 * /app/memory/vendor_registry.md + audit file (backend-side).
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Handshake, Loader2, Plus, Pencil, Trash2, ExternalLink, Activity, X,
} from "lucide-react";

const AREA_ORDER = ["Data Feeds", "Communications", "Payments", "Security", "Infrastructure", "Other"];

const EMPTY_FORM = {
    name: "", area: "Other", service: "", billing_type: "recurring", frequency: "monthly",
    cost_amount: "", renewal_date: "", url: "", notes: "", impacted_features: "", env_keys: "",
    live_status_provider: "", feature_ids: [], key_expires_at: "", key_rotation_note: "",
    owner_email: "",
};

const RENEWAL_BADGE = {
    overdue: "bg-red-500/15 text-red-400 border-red-500/40",
    upcoming: "bg-amber-500/15 text-amber-400 border-amber-500/40",
    ok: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
};

export default function VendorRegistrySection({ readOnly = false, highlightVendor = null, onHighlightDone }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(null);
    const [busy, setBusy] = useState(false);
    const [testPlan, setTestPlan] = useState(null);
    const [featureOptions, setFeatureOptions] = useState([]);
    // iter86.58 — flash + scroll to a vendor row when jumped from Canon → Features.
    const [flashName, setFlashName] = useState(null);

    useEffect(() => {
        if (!highlightVendor || loading) return;
        setFlashName(highlightVendor);
        const t1 = setTimeout(() => {
            const el = document.querySelector(`[data-vendor-name="${CSS.escape(highlightVendor)}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
        const t2 = setTimeout(() => { setFlashName(null); onHighlightDone?.(); }, 3000);
        return () => { clearTimeout(t1); clearTimeout(t2); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlightVendor, loading]);

    // iter86.2 — canonical feature ids from the Feature Registry for the picker.
    useEffect(() => {
        api.featuresRegistry()
            .then((r) => setFeatureOptions((r.rows || []).map((x) => x.feature_id)))
            .catch(() => {});
    }, []);

    const load = useCallback(async () => {
        try {
            const r = await api.vendorsList();
            setData(r);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load vendors");
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const openAdd = () => setForm({ ...EMPTY_FORM });
    const openEdit = (v) => setForm({
        id: v.id, name: v.name, area: v.area || "Other", service: v.service || "",
        billing_type: v.billing_type, frequency: v.frequency,
        cost_amount: v.cost_amount != null ? String(v.cost_amount) : "",
        renewal_date: v.renewal_date ? v.renewal_date.slice(0, 10) : "",
        url: v.url || "", notes: v.notes || "",
        impacted_features: (v.impacted_features || []).join(", "),
        env_keys: (v.env_keys || []).join(", "),
        live_status_provider: v.live_status_provider || "",
        feature_ids: v.feature_ids || [],
        key_expires_at: v.key_expires_at ? v.key_expires_at.slice(0, 10) : "",
        key_rotation_note: v.key_rotation_note || "",
        owner_email: v.owner_email || "",
    });

    const save = async () => {
        if (!form.name.trim()) { toast.error("Vendor name is required"); return; }
        setBusy(true);
        const body = {
            name: form.name.trim(),
            area: form.area,
            service: form.service.trim(),
            billing_type: form.billing_type,
            frequency: form.frequency,
            cost_amount: form.cost_amount !== "" && !isNaN(Number(form.cost_amount)) ? Number(form.cost_amount) : null,
            renewal_date: form.renewal_date || null,
            url: form.url.trim() || null,
            notes: form.notes.trim() || null,
            impacted_features: form.impacted_features.split(",").map((s) => s.trim()).filter(Boolean),
            env_keys: form.env_keys.split(",").map((s) => s.trim()).filter(Boolean),
            live_status_provider: form.live_status_provider || null,
            feature_ids: form.feature_ids || [],
            key_expires_at: form.key_expires_at || null,
            key_rotation_note: form.key_rotation_note.trim() || null,
            owner_email: form.owner_email.trim() || null,
        };
        try {
            if (form.id) {
                const r = await api.vendorUpdate(form.id, body);
                toast.success(`${body.name} updated — registry markdown refreshed`);
                // iter85.5 — a service-affecting change (keys / provider /
                // features) triggers the re-test checklist for this vendor.
                const risky = ["env_keys", "live_status_provider", "impacted_features", "feature_ids"];
                if ((r.changed_fields || []).some((f) => risky.includes(f)) && (r.impacted_features || []).length > 0) {
                    setTestPlan({
                        vendor: r,
                        checked: Object.fromEntries(r.impacted_features.map((f) => [f, true])),
                        note: "",
                    });
                }
            } else {
                await api.vendorCreate(body);
                toast.success(`${body.name} added — registry markdown refreshed`);
            }
            setForm(null);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
        setBusy(false);
    };

    const remove = async (v) => {
        if (!window.confirm(`Remove ${v.name} from the vendor registry?`)) return;
        try {
            await api.vendorDelete(v.id);
            toast.success(`${v.name} removed — registry markdown refreshed`);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
    };

    const rows = data?.rows || [];
    const grouped = AREA_ORDER
        .map((area) => ({ area, rows: rows.filter((v) => (v.area || "Other") === area) }))
        .filter((g) => g.rows.length > 0);

    return (
        <section className="brutal-card" data-testid="vendor-registry-section">
            {testPlan && <TestPlanModal plan={testPlan} onClose={() => setTestPlan(null)} setPlan={setTestPlan} />}
            <div className="p-4 flex items-center gap-3 flex-wrap">
                <Handshake className="w-5 h-5 text-cyber shrink-0" />
                <div className="flex-1 min-w-[200px]">
                    <h3 className="font-display font-extrabold text-base uppercase">Vendor & Contract Registry</h3>
                    <p className="text-[11px] text-zinc-500">
                        {readOnly
                            ? "Read-only mirror — the product's third-party dependency layer. Managed in Commons Console → Vendors."
                            : "Grouped by area. Changes write through to memory/vendor_registry.md + audit trail automatically."}
                    </p>
                </div>
                {data && data.overdue > 0 && (
                    <span className={`pill text-[10px] font-bold uppercase border ${RENEWAL_BADGE.overdue}`} data-testid="vendor-overdue-pill">
                        {data.overdue} overdue
                    </span>
                )}
                {data && data.upcoming > 0 && (
                    <span className={`pill text-[10px] font-bold uppercase border ${RENEWAL_BADGE.upcoming}`} data-testid="vendor-upcoming-pill">
                        {data.upcoming} renewing ≤30d
                    </span>
                )}
                {!readOnly && (
                    <button type="button" onClick={openAdd} data-testid="vendor-add-btn" className="brutal-btn-primary text-xs py-2">
                        <Plus className="w-3.5 h-3.5" /> Add vendor
                    </button>
                )}
            </div>

            {form && (
                <div className="mx-4 mb-4 brutal-card-light p-3 space-y-2" data-testid="vendor-form">
                    <div className="flex items-center justify-between">
                        <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">
                            {form.id ? "Edit vendor" : "New vendor"}
                        </p>
                        <button type="button" onClick={() => setForm(null)} data-testid="vendor-form-close" className="brutal-btn-ghost text-[10px] py-1 px-2">
                            <X className="w-3 h-3" /> Close
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <input data-testid="vendor-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Vendor name *" className="brutal-input text-xs" />
                        <select data-testid="vendor-form-area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} className="brutal-input text-xs">
                            {AREA_ORDER.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                        <input data-testid="vendor-form-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="Service (what they do for us)" className="brutal-input text-xs" />
                        <select data-testid="vendor-form-billing" value={form.billing_type} onChange={(e) => setForm({ ...form, billing_type: e.target.value })} className="brutal-input text-xs">
                            <option value="recurring">Recurring</option>
                            <option value="one_time">One-time</option>
                            <option value="usage_based">Usage-based</option>
                            <option value="free">Free</option>
                        </select>
                        <select data-testid="vendor-form-frequency" value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="brutal-input text-xs">
                            <option value="monthly">Monthly</option>
                            <option value="semiannual">Semi-annual (6-mo)</option>
                            <option value="annual">Annual</option>
                            <option value="usage_based">Usage-based</option>
                            <option value="none">N/A</option>
                        </select>
                        <input data-testid="vendor-form-cost" type="number" min="0" step="0.01" value={form.cost_amount} onChange={(e) => setForm({ ...form, cost_amount: e.target.value })} placeholder="Cost $ (per cycle)" className="brutal-input text-xs" />
                        <label className="flex flex-col gap-0.5">
                            <span className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold">Renewal date</span>
                            <input data-testid="vendor-form-renewal" type="date" value={form.renewal_date} onChange={(e) => setForm({ ...form, renewal_date: e.target.value })} title="Renewal or next billing date" className="brutal-input text-xs" />
                        </label>
                        <label className="flex flex-col gap-0.5">
                            <span className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold">Key expires</span>
                            <input data-testid="vendor-form-key-expiry" type="date" value={form.key_expires_at} onChange={(e) => setForm({ ...form, key_expires_at: e.target.value })} title="API key / token hard expiry — Key rotation radar warns ≤14d before" className="brutal-input text-xs" />
                        </label>
                        <input data-testid="vendor-form-key-note" value={form.key_rotation_note} onChange={(e) => setForm({ ...form, key_rotation_note: e.target.value })} placeholder="Key rotation note (e.g., 90-day policy)" className="brutal-input text-xs" />
                        <input data-testid="vendor-form-owner-email" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} placeholder="Owner email (who owns this vendor account)" className="brutal-input text-xs" title="Operator email that owns this vendor account — resolves the audit trail when a key rolls or the account gets shut off" />
                        <input data-testid="vendor-form-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://vendor.com" className="brutal-input text-xs sm:col-span-2" />
                        <input data-testid="vendor-form-features" value={form.impacted_features} onChange={(e) => setForm({ ...form, impacted_features: e.target.value })} placeholder="Impacted features (comma-separated)" className="brutal-input text-xs sm:col-span-2" />
                        <input data-testid="vendor-form-envkeys" value={form.env_keys} onChange={(e) => setForm({ ...form, env_keys: e.target.value })} placeholder="Env keys (comma-separated)" className="brutal-input text-xs" />
                        <input data-testid="vendor-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes (contract terms, gotchas…)" className="brutal-input text-xs sm:col-span-3" />
                        {featureOptions.length > 0 && (
                            <div className="sm:col-span-3 space-y-1" data-testid="vendor-form-feature-picker">
                                <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold">
                                    Feature IDs (canonical — from <code>feature_registry.md</code>, powers impact radar)
                                </p>
                                <div className="flex flex-wrap gap-1">
                                    {featureOptions.map((fid) => {
                                        const on = (form.feature_ids || []).includes(fid);
                                        return (
                                            <button
                                                key={fid}
                                                type="button"
                                                data-testid={`vendor-feature-chip-${fid.replace(/\./g, "-")}`}
                                                onClick={() => setForm({
                                                    ...form,
                                                    feature_ids: on
                                                        ? form.feature_ids.filter((f) => f !== fid)
                                                        : [...(form.feature_ids || []), fid],
                                                })}
                                                className={`pill text-[9px] font-mono border transition-colors ${on ? "border-fuchsia-500/60 text-fuchsia-300 bg-fuchsia-500/10" : "border-zinc-700 text-zinc-500 hover:text-zinc-300"}`}
                                            >
                                                {fid}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                    <button type="button" onClick={save} disabled={busy} data-testid="vendor-form-save" className="brutal-btn-primary text-xs py-2 disabled:opacity-60">
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                        {form.id ? "Save changes" : "Add vendor"}
                    </button>
                </div>
            )}

            <div className="px-4 pb-4 space-y-4">
                {loading ? (
                    <div className="flex items-center justify-center gap-2 p-6 text-zinc-400 text-xs">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading vendors…
                    </div>
                ) : rows.length === 0 ? (
                    <p className="text-xs text-zinc-500 italic text-center p-4" data-testid="vendor-empty">No vendors yet.</p>
                ) : grouped.map(({ area, rows: areaRows }) => (
                    <div key={area} data-testid={`vendor-area-${area.toLowerCase().replace(/\s+/g, "-")}`}>
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-2">
                            <span className="text-cyber">{area}</span> · {areaRows.length}
                        </p>
                        <div className="space-y-2">
                            {areaRows.map((v) => (
                                <VendorRow key={v.id} v={v} readOnly={readOnly} flash={flashName === v.name} onEdit={() => openEdit(v)} onDelete={() => remove(v)} />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

// iter85.5 — Vendor-change test plan: shown after a service-affecting edit
// (env keys / provider / features). Confirms which impacted features must
// be re-tested and logs the plan to the audit trail.
function TestPlanModal({ plan, setPlan, onClose }) {
    const [logging, setLogging] = useState(false);
    const features = plan.vendor.impacted_features || [];
    const checkedList = features.filter((f) => plan.checked[f]);

    const toggle = (f) => setPlan({ ...plan, checked: { ...plan.checked, [f]: !plan.checked[f] } });

    const log = async () => {
        if (checkedList.length === 0) { toast.error("Select at least one feature to verify"); return; }
        setLogging(true);
        try {
            await api.vendorTestPlan(plan.vendor.id, { features: checkedList, note: plan.note.trim() || null });
            toast.success("Test plan logged to the vendor audit trail");
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to log test plan");
        }
        setLogging(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="vendor-test-plan-modal">
            <div className="brutal-card p-5 max-w-lg w-full space-y-3">
                <div className="flex items-start gap-2">
                    <Activity className="w-5 h-5 text-volt shrink-0 mt-0.5" />
                    <div>
                        <h4 className="font-display font-extrabold text-sm uppercase">Vendor change — re-test checklist</h4>
                        <p className="text-[11px] text-zinc-500">
                            You changed a service-affecting field on <strong className="text-white">{plan.vendor.name}</strong>.
                            These features depend on it — confirm what needs verification and log the plan.
                        </p>
                    </div>
                </div>
                <div className="space-y-1.5 max-h-60 overflow-y-auto" data-testid="vendor-test-plan-features">
                    {features.map((f) => (
                        <label key={f} className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!plan.checked[f]}
                                onChange={() => toggle(f)}
                                data-testid={`test-plan-check-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="accent-[#facc15]"
                            />
                            {f}
                        </label>
                    ))}
                </div>
                <input
                    data-testid="vendor-test-plan-note"
                    value={plan.note}
                    onChange={(e) => setPlan({ ...plan, note: e.target.value })}
                    placeholder="Note (what changed, ticket #…)"
                    className="brutal-input text-xs w-full"
                />
                <div className="flex gap-2 justify-end">
                    <button type="button" onClick={onClose} data-testid="vendor-test-plan-skip" className="brutal-btn-ghost text-xs py-2">
                        <X className="w-3.5 h-3.5" /> Skip
                    </button>
                    <button type="button" onClick={log} disabled={logging} data-testid="vendor-test-plan-log" className="brutal-btn-primary text-xs py-2 disabled:opacity-60">
                        {logging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                        Log {checkedList.length} feature{checkedList.length === 1 ? "" : "s"} to audit trail
                    </button>
                </div>
            </div>
        </div>
    );
}

function VendorRow({ v, readOnly = false, flash = false, onEdit, onDelete }) {
    const [live, setLive] = useState(null);
    const [liveBusy, setLiveBusy] = useState(false);
    // iter87.193.3 — Standalone loading flag for the FORCE SYNC NOW
    // button (renders only on the Odds API row). Kept separate from
    // `liveBusy` so refresh-status and force-sync can't collide.
    const [syncingOdds, setSyncingOdds] = useState(false);

    const loadLive = async (id) => {
        try {
            const r = await api.vendorLiveStatus(id);
            setLive(r);
        } catch { /* silent — the refresh-status button surfaces the failure */ }
    };

    const checkLive = async () => {
        setLiveBusy(true);
        try {
            const r = await api.vendorLiveStatus(v.id);
            setLive(r);
            if (!r.ok) toast.warning(`${v.name} live status unavailable`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Live status failed");
        }
        setLiveBusy(false);
    };

    return (
        <div className={`brutal-card-light p-3 space-y-1.5 transition-shadow ${flash ? "ring-2 ring-cyber" : ""}`} data-vendor-name={v.name} data-testid={`vendor-row-${v.id}`}>
            <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold text-white text-sm">{v.name}</span>
                <span className="pill text-[9px] uppercase border-[#333] text-zinc-400">{v.billing_type.replace("_", "-")}</span>
                {v.frequency !== "none" && (
                    <span className="pill text-[9px] uppercase border-[#333] text-zinc-500">{v.frequency.replace("_", "-")}</span>
                )}
                {v.cost_amount != null && (
                    <span className="pill text-[9px] font-bold border-volt/40 text-volt" data-testid={`vendor-cost-${v.id}`}>
                        ${Number(v.cost_amount).toLocaleString()}{v.frequency === "monthly" ? "/mo" : v.frequency === "semiannual" ? "/6mo" : v.frequency === "annual" ? "/yr" : ""}
                    </span>
                )}
                {v.renewal_date && (
                    <span className={`pill text-[9px] font-bold uppercase border ${RENEWAL_BADGE[v.renewal_status] || "border-[#333] text-zinc-500"}`} data-testid={`vendor-renewal-${v.id}`}>
                        {v.renewal_status === "overdue" ? "overdue" : `renews ${v.renewal_date.slice(0, 10)}`}
                    </span>
                )}
                {v.key_expires_at && (
                    <span className={`pill text-[9px] font-bold uppercase border ${RENEWAL_BADGE[v.key_rotation_status] || "border-[#333] text-zinc-500"}`}
                        title={v.key_rotation_note || "API key / token hard expiry"}
                        data-testid={`vendor-key-expiry-${v.id}`}>
                        {v.key_rotation_status === "overdue" ? "key expired" : `key rotates ${v.key_expires_at.slice(0, 10)}`}
                    </span>
                )}
                <div className="ml-auto flex gap-1.5">
                    {v.url && (
                        <a href={v.url} target="_blank" rel="noreferrer" data-testid={`vendor-url-${v.id}`} className="brutal-btn-ghost text-[10px] py-1 px-2">
                            <ExternalLink className="w-3 h-3" />
                        </a>
                    )}
                    {!readOnly && (
                        <>
                            <button type="button" onClick={onEdit} data-testid={`vendor-edit-${v.id}`} className="brutal-btn-ghost text-[10px] py-1 px-2">
                                <Pencil className="w-3 h-3" /> Edit
                            </button>
                            <button type="button" onClick={onDelete} data-testid={`vendor-delete-${v.id}`} className="brutal-btn-ghost text-[10px] py-1 px-2 text-red-400">
                                <Trash2 className="w-3 h-3" />
                            </button>
                        </>
                    )}
                </div>
            </div>
            {v.service && <p className="text-[11px] text-zinc-400">{v.service}</p>}
            {v.owner_email && (
                <p className="text-[11px] text-zinc-400" data-testid={`vendor-owner-${v.id}`}>
                    <span className="text-zinc-500 uppercase text-[9px] tracking-wider font-bold mr-1.5">Owner</span>
                    <a href={`mailto:${v.owner_email}`} className="text-volt hover:underline">{v.owner_email}</a>
                </p>
            )}
            {v.notes && <p className="text-[11px] text-zinc-500 italic">{v.notes}</p>}
            {((v.impacted_features || []).length > 0 || (v.env_keys || []).length > 0 || (v.feature_ids || []).length > 0) && (
                <div className="flex flex-wrap gap-1">
                    {(v.feature_ids || []).map((f) => (
                        <span key={f} className="pill text-[9px] font-mono border-fuchsia-500/40 text-fuchsia-300" data-testid={`vendor-fid-${v.id}-${f.replace(/\./g, "-")}`}>{f}</span>
                    ))}
                    {(v.impacted_features || []).map((f) => (
                        <span key={f} className="pill text-[9px] border-cyber/30 text-cyber">{f}</span>
                    ))}
                    {(v.env_keys || []).map((k) => (
                        <span key={k} className="pill text-[9px] border-volt/30 text-volt font-mono">{k}</span>
                    ))}
                </div>
            )}
            {v.live_status_provider && (
                <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-[#1c1c1c]" data-testid={`vendor-live-${v.id}`}>
                    {v.live_status_shelved ? (
                        <>
                            <button type="button" disabled data-testid={`vendor-live-btn-${v.id}`} className="brutal-btn-ghost text-[10px] py-1 px-2 opacity-40 cursor-not-allowed">
                                <Activity className="w-3 h-3" /> Live status
                            </button>
                            <span className="pill text-[9px] uppercase border-[#333] text-zinc-500" data-testid={`vendor-live-shelved-${v.id}`}>
                                Shelved — sending-only key, no domain check
                            </span>
                        </>
                    ) : (
                        <button type="button" onClick={checkLive} disabled={liveBusy} data-testid={`vendor-live-btn-${v.id}`} className="brutal-btn-ghost text-[10px] py-1 px-2 disabled:opacity-60">
                            {liveBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />}
                            {liveBusy ? "Checking…" : "Live status"}
                        </button>
                    )}
                    {live?.ok && live.provider === "api_sports" && (
                        <>
                            <span className="pill text-[9px] uppercase border-volt/40 text-volt">{live.subscription?.plan || "plan?"}</span>
                            {live.subscription?.end && <span className="pill text-[9px] border-[#333] text-zinc-400">ends {String(live.subscription.end).slice(0, 10)}</span>}
                            <span className="pill text-[9px] border-cyber/40 text-cyber">{live.requests?.current ?? "?"}/{live.requests?.limit_day ?? "?"} requests today</span>
                        </>
                    )}
                    {live?.ok && live.provider === "resend" && (
                        live.domains.length === 0
                            ? <span className="text-[10px] text-amber-400">No sending domains configured</span>
                            : live.domains.map((d) => (
                                <span key={d.name} className={`pill text-[9px] font-mono border ${d.status === "verified" ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`}>
                                    {d.name} · {d.status}
                                </span>
                            ))
                    )}
                    {live?.ok && live.provider === "anthropic" && (
                        <>
                            <span className="pill text-[9px] border-emerald-500/40 text-emerald-400">key valid · {live.models_count} models</span>
                            <span className={`pill text-[9px] uppercase ${live.routes_ride_story ? "border-neon/40 text-neon" : "border-[#333] text-zinc-500"}`}>
                                {live.routes_ride_story ? "Active — routes Ride Story" : "Idle — Emergent key routes Ride Story here"}
                            </span>
                        </>
                    )}
                    {live?.ok && live.provider === "emergent_llm" && (
                        <>
                            <span className="pill text-[9px] border-cyber/40 text-cyber">{live.calls_30d} LLM calls / 30d · ~${live.est_cost_30d_usd}</span>
                            <span className={`pill text-[9px] ${live.last_success_ago ? "border-emerald-500/40 text-emerald-400" : "border-[#333] text-zinc-500"}`}>
                                {live.last_success_ago ? `last success ${live.last_success_ago} (${live.last_success?.feature})` : "no successful calls logged yet"}
                            </span>
                        </>
                    )}
                    {live?.ok && live.provider === "the_odds_api" && (
                        <>
                            <span className="pill text-[9px] border-cyber/40 text-cyber" data-testid={`vendor-live-odds-quota-${v.id}`}>
                                {live.requests?.current ?? "?"}/{live.requests?.limit_month ?? "?"} requests this month
                            </span>
                            <span className={`pill text-[9px] uppercase ${live.environment === "production" && live.schedule_enabled ? "border-neon/40 text-neon" : "border-[#333] text-zinc-500"}`}>
                                {live.environment === "production" && live.schedule_enabled ? "scheduler on" : "scheduler off (preview safe)"}
                            </span>
                            {live.last_snapshot_at && (
                                <span className="pill text-[9px] border-[#333] text-zinc-400">snap {String(live.last_snapshot_at).slice(0, 10)}</span>
                            )}
                            <button
                                type="button"
                                data-testid="odds-force-sync-btn"
                                disabled={syncingOdds}
                                onClick={async () => {
                                    setSyncingOdds(true);
                                    try {
                                        const r = await api.oddsSpreadSyncManual();
                                        toast.success(
                                            `Odds sync: ${r.total_matched} rows written across ${r.sports_polled} sports · ` +
                                            `${r.results?.[r.results.length - 1]?.x_requests_remaining ?? "?"}/500 requests remaining`
                                        );
                                        // Refresh live-status pill from the fresh cache snapshot.
                                        loadLive(v.id);
                                    } catch (err) {
                                        toast.error(err.response?.data?.detail || "Odds sync failed");
                                    }
                                    setSyncingOdds(false);
                                }}
                                className="pill text-[9px] uppercase font-bold border-cyber bg-cyber/10 text-cyber hover:bg-cyber/20 hover:brightness-110 disabled:opacity-60 disabled:cursor-wait transition"
                                title="Manually trigger the Odds API bulk poller. Bypasses the scheduler window. Consumes ~5-6 upstream calls (one per active sport) from the 500/month budget. Use only when awaiting an auto-window or after a fresh deploy."
                            >
                                {syncingOdds ? "syncing…" : "force sync now"}
                            </button>
                        </>
                    )}
                    {live && !live.ok && (
                        <span className="text-[10px] text-red-400" data-testid={`vendor-live-error-${v.id}`}>Unavailable: {live.error}</span>
                    )}
                </div>
            )}
        </div>
    );
}
